import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Building2, Users, Inbox, MessageSquare, Sparkles, DollarSign,
  Activity, CheckCircle2, AlertCircle, ExternalLink,
} from "lucide-react";

const HEALTH_DOT = { up: "var(--n-success)", down: "var(--n-danger)", configured: "var(--n-success)", not_configured: "var(--n-warning)", missing_key: "var(--n-danger)", unknown: "var(--n-text-muted)" };

function Metric({ label, value, hint, icon: Icon, accent }) {
  return (
    <div className="n-card p-5" data-testid={`platform-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="n-label">{label}</div>
        {Icon && <Icon size={14} style={{ color: accent || "var(--n-text-muted)" }} />}
      </div>
      <div className="font-cab font-black text-4xl n-stat mt-3" style={{ color: accent || "inherit" }}>{value}</div>
      {hint && <div className="text-xs text-[var(--n-text-muted)] mt-2 font-mono-plex">{hint}</div>}
    </div>
  );
}

export default function PlatformDashboard() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    api.get("/platform/summary").then(({ data }) => setData(data)).catch(() => {});
    api.get("/platform/health").then(({ data }) => setHealth(data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 text-[var(--n-text-muted)]">Loading platform…</div>;

  const t = data.totals;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="platform-dashboard">
      <div className="flex items-baseline justify-between mb-8 fade-up">
        <div>
          <div className="n-label">Nariai · Master control</div>
          <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Platform overview</h1>
        </div>
        <Link
          to="/admin/tenants"
          className="n-btn flex items-center gap-2"
          data-testid="platform-new-tenant-cta"
        >
          Manage tenants →
        </Link>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Metric label="Total tenants" value={t.tenants} hint={`${t.active_tenants} active`} icon={Building2} />
        <Metric label="Total leads" value={t.leads} hint="across all tenants" icon={Inbox} />
        <Metric label="Conversations" value={t.conversations} hint={`${t.ai_messages} AI replies`} icon={MessageSquare} />
        <Metric label="Users" value={t.users} hint="platform + tenant" icon={Users} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Metric label="Messages" value={t.messages} hint="sent + received" icon={Sparkles} />
        <Metric label="AI replies" value={t.ai_messages} hint="all-time" icon={Sparkles} accent="var(--n-success)" />
        <Metric label="Revenue" value={`$${t.revenue_usd || 0}`} hint="Stripe not wired yet" icon={DollarSign} />
        <Metric label="Uptime" value={health?.api === "up" ? "100%" : "—"} hint="preview env" icon={Activity} accent="var(--n-success)" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Timeseries */}
        <div className="n-card p-5 lg:col-span-2" data-testid="platform-timeseries">
          <div className="n-label">Leads across platform · last 14 days</div>
          <div className="font-cab font-bold text-lg mt-1 mb-4">Cross-tenant acquisition</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={data.timeseries}>
                <CartesianGrid stroke="#27272A" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#71717A" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272A", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Line type="monotone" dataKey="leads" stroke="#00CC66" strokeWidth={2} dot={{ r: 2, fill: "#00CC66" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Platform health */}
        <div className="n-card p-5" data-testid="platform-health-card">
          <div className="n-label">Platform health</div>
          <div className="font-cab font-bold text-lg mt-1 mb-4">System status</div>
          {health && (
            <div className="space-y-3">
              {[
                ["API server", health.api],
                ["Database", health.db],
                ["AI (Claude)", health.ai],
                ["Twilio SMS", health.twilio],
                ["Email", health.email],
              ].map(([label, status]) => (
                <div key={label} className="flex items-center justify-between text-sm" data-testid={`health-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full pulse-dot" style={{ background: HEALTH_DOT[status] || "var(--n-text-muted)" }} />
                    <span className="text-[var(--n-text-sec)]">{label}</span>
                  </div>
                  <span className="text-xs font-mono-plex uppercase text-[var(--n-text-muted)]">{status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Per-tenant business health */}
      <div className="n-card overflow-hidden" data-testid="business-health-table">
        <div className="p-5 border-b border-[var(--n-border)]">
          <div className="n-label">Business health</div>
          <div className="font-cab font-bold text-lg mt-1">Per-tenant snapshot</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] border-b border-[var(--n-border)]">
            <tr className="text-left">
              {["Brand", "Industry", "Domain", "Status", "Leads", "Conversations", "Last activity", ""].map((h) => (
                <th key={h} className="n-label py-2 px-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.tenants.map((t) => (
              <tr key={t.id} className="border-b border-[var(--n-border)]" data-testid={`business-row-${t.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: t.primary_color }} />
                    <span className="font-cab font-bold">{t.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-[var(--n-text-sec)] font-mono-plex text-xs">{t.industry || "—"}</td>
                <td className="px-4 py-3 text-[var(--n-text-sec)] font-mono-plex text-xs">{t.domain || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`n-chip ${t.status === "active" ? "text-[var(--n-success)]" : t.status === "suspended" ? "text-[var(--n-danger)]" : ""}`}>
                    {t.status || "active"}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono-plex n-stat">{t.leads}</td>
                <td className="px-4 py-3 font-mono-plex n-stat">{t.conversations}</td>
                <td className="px-4 py-3 text-xs font-mono-plex text-[var(--n-text-muted)]">
                  {t.last_lead_at ? new Date(t.last_lead_at).toLocaleDateString() : "no leads yet"}
                </td>
                <td className="px-4 py-3">
                  <a href={`/site/${t.slug}`} target="_blank" rel="noreferrer" className="text-[var(--n-text-sec)] hover:text-white" data-testid={`business-visit-${t.slug}`}>
                    <ExternalLink size={14} />
                  </a>
                </td>
              </tr>
            ))}
            {data.tenants.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-[var(--n-text-muted)]">No tenants yet. Create your first from the Tenants page.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
